<script setup>

import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import CustomMultiselect from "@/components/global-themed/CustomMultiselect.vue";
import {ref} from "vue";
import {useMutation} from "@tanstack/vue-query";
import axios from "axios";
import {errorToast, successToast} from "@/composables/useToasts";
import useWebsite from "@/composables/useWebsite";
import {useStore} from "vuex";

defineProps({
  reasons: {
    type: Object,
    required: true,
  },
});

const reason = ref('');

const {websiteId, isLoading: isWebsitePending} = useWebsite();
const store = useStore();

const {mutate: deleteWebsite, isPending} = useMutation({
  mutationFn: async () => await axios.post(`/ajax/v2/billing/package/website/${websiteId.value}/delete`, {
    reason: reason.value,
  }),
  onError: () => {
    errorToast('Something went wrong');
  },
  onSuccess: ({data}) => {
    successToast(data.message);

    window.location.href = data.redirectUrl;
  },
});
</script>

<template>
  <div class="tw-m-auto tw-max-w-default-container">
    <div class="tw-bg-white tw-px-64 tw-py-18 tw-rounded-lg tw-flex tw-flex-col tw-gap-y-8 tw-items-center">
      <h1 class="tw-font-bold tw-text-4.5xl tw-text-neutral-900">
        Do you want to remove this website?
      </h1>

      <div class="tw-space-y-4 tw-text-base tw-text-neutral-900">
        <div>
          <p>
            Deleting this website will permanently erase all related data, including:
          </p>
          <ul class="tw-list-disc tw-list-inside">
            <li>Keyword Strategy</li>
            <li>Keyword Ideas</li>
            <li>Keyword Tracking</li>
            <li>Website Actions</li>
            <li>Backlink Analysis</li>
            <li>Content Ideas</li>
          </ul>
          <p>
            You will also lose access to the Onsite Optimizer for this site.
          </p>
        </div>

        <div class="tw-text-xl tw-font-bold tw-leading-9">
          Important Notice
        </div>

        <p>
          <span class="tw-font-bold">Important Notice:</span>
          You are currently using the Onsite Optimizer for this website. Removing the website will prevent you from making any new edits or updating existing changes.
        </p>

        <p>
          However, all changes you’ve already implemented will remain active on the client’s site, provided the script stays installed.
        </p>

        <p>
          If you plan to remove the script, we strongly recommend downloading a CSV file of all SEO changes made. This record will help you (or your client) reapply these changes manually in the future, if needed.
        </p>

        <div class="tw-py-3">
          <DefaultButton
            @click="store.dispatch('onsiteEditor/fetchChangesCsv', websiteId)"
          >
            Download CSV File
          </DefaultButton>
        </div>

        <p class="tw-text-center">
          This information is <strong class="tw-text-danger">permanently deleted</strong> and the data cannot be
          retrieved if you change your mind.
        </p>
      </div>

      <div class="tw-w-96">
        <label class="tw-text-neutral-900">Reason for deleting:</label>
        <CustomMultiselect
          v-model="reason"
          class="tw-text-left tw-w-full"
          :close-on-select="true"
          :options="reasons"
          :searchable="true"
          :can-deselect="false"
          placeholder="Please select a reason"
        />
      </div>

      <DefaultButton
        variant="danger"
        :loading="isPending"
        :disabled="!reason || isPending || isWebsitePending"
        @click="deleteWebsite"
      >
        Yes Please Delete Now
      </DefaultButton>
    </div>
  </div>
</template>
